import { type FC } from "react";
import { Card, CardContent, CardHeader, Chip, Stack } from "@mui/material";
import { useSnackbar } from "notistack";
import { FormBuilder } from "@/shared/components/Form";
import type { Schema } from "@/shared/components/Form/types";
import { useUpdatePlatformBunnySettingsMutation } from "@/admin/entities/platforms";
import type { PlatformShowQueryType } from "@/types/graphql/admin.generated";

const schema: Schema = {
  libraryId: {
    type: "text",
    label: "Bunny Library ID",
    required: true,
    xs: 12,
    sm: 6,
    fullWidth: true,
  },
  cdnHostname: {
    type: "text",
    label: "CDN Hostname (vz-...b-cdn.net)",
    required: true,
    xs: 12,
    sm: 6,
    fullWidth: true,
  },
  apiKey: {
    type: "password",
    label: "API Key библиотеки",
    required: true,
    xs: 12,
    fullWidth: true,
  },
  readOnlyApiKey: {
    type: "password",
    label: "Read-Only API Key (подпись вебхуков)",
    required: true,
    xs: 12,
    fullWidth: true,
  },
  cdnTokenKey: {
    type: "password",
    label: "Token Authentication Key pull zone",
    required: true,
    xs: 12,
    fullWidth: true,
  },
  accountApiKey: {
    type: "password",
    label: "Account API Key (необязателен, нужен для расхода)",
    required: false,
    xs: 12,
    fullWidth: true,
  },
};

interface PlatformBunnySettingsCardProps {
  platformId: string;
  bunnySettings: NonNullable<
    PlatformShowQueryType["platform"]
  >["bunnySettings"];
}

export const PlatformBunnySettingsCard: FC<PlatformBunnySettingsCardProps> = ({
  platformId,
  bunnySettings,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [updatePlatformBunnySettings, { loading }] =
    useUpdatePlatformBunnySettingsMutation({
      onCompleted: () => {
        enqueueSnackbar("Bunny Stream подключён", {
          variant: "success",
        });
      },
      onError: (error: Error) => {
        enqueueSnackbar(
          error.message.includes("INVALID_BUNNY_SETTINGS")
            ? "Заполните все обязательные поля"
            : error.message,
          { variant: "error" }
        );
      },
    });

  return (
    <Card>
      <CardHeader
        title="Bunny Stream"
        action={
          bunnySettings?.connectedAt ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" color="success" label="Подключено" />
              <Chip
                size="small"
                variant="outlined"
                label={bunnySettings.libraryId}
              />
            </Stack>
          ) : (
            <Chip size="small" label="Не подключено" />
          )
        }
      />
      <CardContent>
        <FormBuilder
          formId="admin-bunny-settings-form"
          schema={schema}
          initialValues={{
            libraryId: bunnySettings?.libraryId ?? "",
            cdnHostname: bunnySettings?.cdnHostname ?? "",
            apiKey: "",
            readOnlyApiKey: "",
            cdnTokenKey: "",
            accountApiKey: "",
          }}
          onSubmit={async (values) => {
            await updatePlatformBunnySettings({
              variables: {
                platformId,
                input: {
                  libraryId: String(values.libraryId),
                  apiKey: String(values.apiKey),
                  readOnlyApiKey: String(values.readOnlyApiKey),
                  cdnHostname: String(values.cdnHostname),
                  cdnTokenKey: String(values.cdnTokenKey),
                  accountApiKey: values.accountApiKey
                    ? String(values.accountApiKey)
                    : null,
                },
              },
            });
          }}
          submitButton
          resetButton={false}
          submitButtonText="Подключить"
          disabled={loading}
        />
      </CardContent>
    </Card>
  );
};
